import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useParams } from 'react-router-dom';
import { Users, Pill, Calendar, Bell, Activity, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import PatientsPage from './PatientsPage';
import PatientDetailPage from './PatientDetailPage';
import MedicationsPage from './MedicationsPage';
import MedicationDetailPage from './MedicationDetailPage';
import ServicesPage from './ServicesPage';
import ServiceDetailPage from './ServiceDetailPage';
import ApiService from './ApiService';
import UserManagementPage from './UserManagementPage';
import RolesPage from './RolesPage';
import ActivityLogPage from './ActivityLogPage';
import RecycleBinPage from './RecycleBinPage';
import SecurityPage from './SecurityPage';
import AutoLogout from './AutoLogout';
import appConfig from '../config';
import { terminologyFor } from '../terminology';
import ProtocolsOverviewPage from './ProtocolsOverviewPage';
import DuplicateDetailPage from './DuplicateDetailPage';
import LoggedOutPage from './LoggedOutPage';
window.ApiService = ApiService;

// Wrapper component for PatientDetailPage to handle URL params
const PatientDetailWrapper = ({ config, currentUser }) => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPatient = async () => {
      try {
        const patientData = await ApiService.getPatient(patientId);
        setPatient(patientData);
      } catch (err) {
        console.error('Failed to load patient:', err);
        navigate('/patients');
      } finally {
        setLoading(false);
      }
    };
    loadPatient();
  }, [patientId, navigate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!patient) return null;

  return (
    <PatientDetailPage
      patient={patient}
      config={config}
      onBack={() => navigate('/patients')}
      onPatientDeleted={() => navigate('/patients')}
      currentUser={currentUser}
    />
  );
};

// Wrapper for MedicationDetailPage
const MedicationDetailWrapper = ({ config, currentUser }) => {
  const { medicationId } = useParams();
  const navigate = useNavigate();
  const [medication, setMedication] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadMedication = async () => {
      try {
        const medData = await ApiService.getMedication(medicationId);
        setMedication(medData);
      } catch (err) {
        console.error('Failed to load medication:', err);
        navigate('/medications');
      } finally {
        setLoading(false);
      }
    };
    loadMedication();
  }, [medicationId, navigate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!medication) return null;

  return (
    <MedicationDetailPage
      medication={medication}
      config={config}
      onBack={() => navigate('/medications')}
      currentUser={currentUser}
    />
  );
};

// Wrapper for ServiceDetailPage
const ServiceDetailWrapper = ({ config, currentUser }) => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadService = async () => {
      try {
        const serviceData = await ApiService.getService(serviceId);
        setService(serviceData);
      } catch (err) {
        console.error('Failed to load service:', err);
        navigate('/services');
      } finally {
        setLoading(false);
      }
    };
    loadService();
  }, [serviceId, navigate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!service) return null;

  return (
    <ServiceDetailPage
      service={service}
      config={config}
      onBack={() => navigate('/services')}
      currentUser={currentUser}
    />
  );
};

// Dashboard Component
const Dashboard = ({ config }) => {
  // const navigate = useNavigate();
  const [dashboardStats, setDashboardStats] = useState({
    recentPatients: [],
    recentlyAccessedPatients: []
  });
  const [protocolStats, setProtocolStats] = useState({ active: 0, endingSoon: 0, ended: 0, openEnded: 0, reviewDue: 0 });
  const [loading, setLoading] = useState(true);

  const t = config?.terminology || {
    item: { singular: 'Medication', plural: 'Medications' },
    protocol: { singular: 'Protocol', plural: 'Protocols' },
    service: { singular: 'Service', plural: 'Services' },
    client: { singular: 'Patient', plural: 'Patients', singularLower: 'patient', pluralLower: 'patients' }
  };

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const [pStats, patientsResponse, recentlyAccessed] = await Promise.all([
          ApiService.getProtocolStats().catch(() => ({ active: 0, endingSoon: 0, ended: 0, openEnded: 0, reviewDue: 0 })),
          ApiService.getPatients('?limit=1000').catch(() => ({ patients: [] })),
          ApiService.getRecentlyAccessedPatients().catch(() => [])
        ]);
        setProtocolStats(pStats);
        const patients = patientsResponse.patients || patientsResponse || [];
        setDashboardStats({
          recentPatients: patients.slice(0, 5),
          recentlyAccessedPatients: recentlyAccessed
        });
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-gray-500">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Welcome to your clinical console</p>
      </div>

      {/* Protocol stats — clickable, each opens the Protocols Overview filtered to that category */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Link to="/protocols-overview?category=active">
          <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer border-l-4 h-full border-spruce-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active {t.protocol.plural}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{protocolStats.active}</p>
              </div>
              <Activity className="text-spruce-500" size={40} />
            </div>
          </div>
        </Link>

        <Link to="/protocols-overview?category=ending-soon">
          <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer border-l-4 h-full border-amber-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Ending Soon (7d)</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{protocolStats.endingSoon}</p>
              </div>
              <Clock className="text-amber-500" size={40} />
            </div>
          </div>
        </Link>

        <Link to="/protocols-overview?category=ended">
          <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer border-l-4 h-full border-gray-400">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Ended</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{protocolStats.ended}</p>
              </div>
              <CheckCircle className="text-gray-400" size={40} />
            </div>
          </div>
        </Link>

        <Link to="/protocols-overview?category=open-ended">
          <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer border-l-4 h-full border-copper-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Open-ended (no end)</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{protocolStats.openEnded}</p>
                {protocolStats.reviewDue > 0 && (
                  <p className="text-xs font-medium text-copper-700 mt-1">{protocolStats.reviewDue} due for review (6mo+)</p>
                )}
              </div>
              <AlertTriangle className="text-copper-500" size={40} />
            </div>
          </div>
        </Link>
      </div>

      {/* Recently Accessed Patients */}
      {dashboardStats.recentlyAccessedPatients.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Recently Accessed {t.client.plural}</h2>
          <div className="space-y-3">
            {dashboardStats.recentlyAccessedPatients.map((patient) => (
              <Link
                key={patient.patientId}
                to={`/patients/${patient.patientId}`}
                className="flex items-center justify-between p-4 hover:bg-spruce-50 rounded-lg transition border border-gray-200 hover:border-spruce-500"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-spruce-100 flex items-center justify-center">
                    <Users className="text-spruce-600" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{patient.name}</p>
                    <p className="text-sm text-gray-600">{patient.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">{formatTimestamp(patient.lastAccessedAt)}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Recent Patients (fallback if no recently accessed) */}
      {dashboardStats.recentlyAccessedPatients.length === 0 && dashboardStats.recentPatients.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Recent {t.client.plural}</h2>
          <div className="space-y-3">
            {dashboardStats.recentPatients.map((patient) => (
              <Link
                key={patient.patientId}
                to={`/patients/${patient.patientId}`}
                className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition"
              >
                <div>
                  <p className="font-medium text-gray-900">{patient.name}</p>
                  <p className="text-sm text-gray-600">{patient.email}</p>
                </div>
                <span className="text-gray-400">→</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Navigation Component
const Navigation = ({ config, currentUser }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const t = config?.terminology || {
    item: { plural: 'Medications' },
    service: { plural: 'Services' },
    client: { plural: 'Patients' }
  };

  const handleLogout = () => {
    // Clear any local storage/session storage
    localStorage.clear();
    sessionStorage.clear();

    // Redirect to Cognito Hosted UI logout. Values come from config.js, which is
    // generated per-environment from terraform outputs.
    const logoutUri = encodeURIComponent(appConfig.appDomain);
    window.location.href = `${appConfig.cognitoDomain}/logout?client_id=${appConfig.userPoolWebClientId}&logout_uri=${logoutUri}`;
  };

  return (
    <nav className="bg-spruce-900 shadow-md">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center gap-8">
            <Link to="/" className="text-xl font-display font-bold text-white tracking-tight">
              BlackCapHealth
              <span className="block text-[10px] font-data font-normal tracking-[0.18em] text-copper-300 uppercase leading-none mt-0.5">Clinical Console</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex gap-6">
              <Link
                to="/"
                className="text-spruce-200 hover:text-white font-medium transition border-b-2 border-transparent hover:border-copper-400 pb-0.5"
              >
                Dashboard
              </Link>
              <Link
                to="/patients"
                className="text-spruce-200 hover:text-white font-medium transition border-b-2 border-transparent hover:border-copper-400 pb-0.5"
              >
                {t.client.plural}
              </Link>
              <Link
                to="/medications"
                className="text-spruce-200 hover:text-white font-medium transition border-b-2 border-transparent hover:border-copper-400 pb-0.5"
              >
                {t.item.plural}
              </Link>
              <Link
                to="/services"
                className="text-spruce-200 hover:text-white font-medium transition border-b-2 border-transparent hover:border-copper-400 pb-0.5"
              >
                {t.service.plural}
              </Link>

              {currentUser && (currentUser.groups?.includes('Admins') || currentUser.permissions?.includes('users.view')) && (
                <>
                  <Link
                    to="/admin/users"
                    className="text-spruce-200 hover:text-white font-medium transition border-b-2 border-transparent hover:border-copper-400 pb-0.5"
                  >
                    Users
                  </Link>
                  <Link
                    to="/admin/roles"
                    className="text-spruce-200 hover:text-white font-medium transition border-b-2 border-transparent hover:border-copper-400 pb-0.5"
                  >
                    Roles
                  </Link>
                  <Link
                    to="/admin/activity"
                    className="text-spruce-200 hover:text-white font-medium transition border-b-2 border-transparent hover:border-copper-400 pb-0.5"
                  >
                    Activity
                  </Link>
                  <Link
                    to="/admin/recycle-bin"
                    className="text-spruce-200 hover:text-white font-medium transition border-b-2 border-transparent hover:border-copper-400 pb-0.5"
                  >
                    Recycle Bin
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Right side - User info and logout (desktop) */}
          <div className="hidden md:flex items-center gap-4">
            {currentUser && (
              <>
                <Link to="/account/security" className="text-spruce-200 hover:text-white font-medium transition border-b-2 border-transparent hover:border-copper-400 pb-0.5">
                  Security
                </Link>
                <div className="text-sm text-spruce-200">
                  <span className="font-medium">{currentUser.name || currentUser.email}</span>
                  {currentUser.groups?.length > 0 && (
                    <span className="ml-2 text-xs font-data border border-copper-400 text-copper-300 px-2 py-1 rounded-full font-semibold">
                      {currentUser.groups[0]}
                    </span>
                  )}
                </div>
                <button
                  onClick={handleLogout}
                  className="border border-spruce-500 hover:bg-spruce-800 text-spruce-100 px-4 py-2 rounded-lg font-medium transition"
                >
                  Logout
                </button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg text-spruce-100 hover:bg-spruce-800"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-spruce-700">
            <div className="flex flex-col space-y-3">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="text-spruce-200 hover:text-white font-medium py-2 px-2 hover:bg-spruce-800 rounded transition"
              >
                Dashboard
              </Link>
              <Link
                to="/patients"
                onClick={() => setMobileMenuOpen(false)}
                className="text-spruce-200 hover:text-white font-medium py-2 px-2 hover:bg-spruce-800 rounded transition"
              >
                {t.client.plural}
              </Link>
              <Link
                to="/medications"
                onClick={() => setMobileMenuOpen(false)}
                className="text-spruce-200 hover:text-white font-medium py-2 px-2 hover:bg-spruce-800 rounded transition"
              >
                {t.item.plural}
              </Link>
              <Link
                to="/services"
                onClick={() => setMobileMenuOpen(false)}
                className="text-spruce-200 hover:text-white font-medium py-2 px-2 hover:bg-spruce-800 rounded transition"
              >
                {t.service.plural}
              </Link>

              {currentUser && (currentUser.groups?.includes('Admins') || currentUser.permissions?.includes('users.view')) && (
                <>
                  <Link
                    to="/admin/users"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-spruce-200 hover:text-white font-medium py-2 px-2 hover:bg-spruce-800 rounded transition"
                  >
                    Users
                  </Link>
                  <Link
                    to="/admin/roles"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-spruce-200 hover:text-white font-medium py-2 px-2 hover:bg-spruce-800 rounded transition"
                  >
                    Roles
                  </Link>
                </>
              )}

              {/* Mobile User Info & Logout */}
              {currentUser && (
                <div className="pt-3 border-t border-gray-200 mt-3">
                  <div className="text-sm text-gray-600 px-2 py-2">
                    <span className="font-medium block">{currentUser.name || currentUser.email}</span>
                    {currentUser.groups?.length > 0 && (
                      <span className="mt-1 inline-block text-xs bg-spruce-100 text-spruce-800 px-2 py-1 rounded-full font-semibold">
                        {currentUser.groups[0]}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full mt-2 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium transition text-left"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Main App Component with Router
// App.js renders <AdminApp> without a config prop, so default to the imported
// singleton — this is the object whose `terminology` we swap per-org below and
// pass to every child.
const AdminApp = ({ config = appConfig }) => {
  const [currentUser, setCurrentUser] = useState(null);
  useEffect(() => {
    const loadUser = async () => {
      // Don't try to load user on logged-out page
      if (window.location.pathname === '/logged-out') {
        return;
      }
      
      try {
        const user = await ApiService.getCurrentUser();
        // Swap in the org's terminology preset before the authed UI renders, so
        // every consumer of config.terminology picks up the tenant's vocabulary.
        config.terminology = terminologyFor(user?.orgType);
        setCurrentUser(user);
      } catch (err) {
        console.error('Failed to load user:', err);
      }
    };
    loadUser();
  }, []);

  // Helper function to check if user has role
  const hasRole = (roles) => {
    if (!currentUser || !currentUser.groups) return false;
    if (currentUser.groups.includes('Admins')) return true; // Admins can do everything
    return roles.some(role => currentUser.groups.includes(role));
  };

  return (
    <Router>
      {currentUser && (
        <AutoLogout
          timeoutMinutes={15} //15
          warningMinutes={3} //3
        />
      )}

      <Routes>
        {/* PUBLIC ROUTE - No layout, no auth check */}
        <Route path="/logged-out" element={<LoggedOutPage />} />

        {/* PROTECTED ROUTES - With layout */}
        <Route path="*" element={
          <div className="min-h-screen bg-porcelain">
            <Navigation config={config} currentUser={currentUser} />
            <div className="max-w-7xl mx-auto px-4 py-8">
              <Routes>
                <Route path="/admin/roles" element={<RolesPage currentUser={currentUser} />} />
                <Route path="/admin/activity" element={<ActivityLogPage />} />
                <Route path="/admin/recycle-bin" element={<RecycleBinPage />} />
                <Route path="/account/security" element={<SecurityPage />} />
                <Route path="/" element={<Dashboard config={config} />} />
                <Route path="/protocols-overview" element={<ProtocolsOverviewPage config={config} />} />
                <Route path="/patients" element={<PatientsPage config={config} currentUser={currentUser} hasRole={hasRole} />} />
                <Route path="/patients/:patientId" element={<PatientDetailWrapper config={config} currentUser={currentUser} />} />
                <Route path="/duplicates" element={<DuplicateDetailPage config={config} currentUser={currentUser} />} />
                <Route path="/medications" element={<MedicationsPage config={config} currentUser={currentUser} />} />
                <Route path="/medications/:medicationId" element={<MedicationDetailWrapper config={config} currentUser={currentUser} />} />
                <Route path="/services" element={<ServicesPage config={config} currentUser={currentUser} />} />
                <Route path="/services/:serviceId" element={<ServiceDetailWrapper config={config} currentUser={currentUser} />} />
                <Route path="/admin/users" element={<UserManagementPage currentUser={currentUser} />} />
              </Routes>
            </div>
          </div>
        } />
      </Routes>
    </Router>
  );
};

export default AdminApp;